import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Check } from 'lucide-react'
import { createHome } from '@/services/home.api'
import { useProperty } from '@/contexts/PropertyContext'
import { GoogleAddressAutocomplete } from '@/components/address/GoogleAddressAutocomplete'

interface AddHomeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'India',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Mexico',
  'Brazil',
  'Japan',
  'China',
  'Singapore',
  'United Arab Emirates',
  'Other',
]

export function AddHomeModal({ open, onOpenChange }: AddHomeModalProps) {
  const { t } = useTranslation('home')
  const { refreshProperties } = useProperty()

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [country, setCountry] = useState('United States')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const reset = () => {
    setName('')
    setAddress('')
    setCity('')
    setState('')
    setZip('')
    setCountry('United States')
    setError(null)
    setSuccess(false)
    setIsSubmitting(false)
  }

  const handleOpenChange = (value: boolean) => {
    if (!value) reset()
    onOpenChange(value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!address.trim() || !city.trim() || !zip.trim() || !country.trim()) {
      setError(t('addHome.addressRequiredError'))
      return
    }
    if (state.trim() && state.trim().length !== 2) {
      setError('State must be a 2-letter code (e.g. FL, CA, NY)')
      return
    }
    if (zip.trim() && !/^\d{5}(-\d{4})?$/.test(zip.trim())) {
      setError('Zip code must be 5 digits (e.g. 33101)')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await createHome({
        name: name.trim() || undefined,
        address: address.trim(),
        city: city.trim(),
        state: state.trim() || undefined,
        zip: zip.trim(),
        country: country.trim(),
      })

      setSuccess(true)
      await refreshProperties()

      setTimeout(() => {
        handleOpenChange(false)
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('addHome.submitError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('addHome.title')}</DialogTitle>
          <DialogDescription>
            {t('addHome.description')}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-lg font-medium text-green-600">{t('addHome.successMessage')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('addHome.nameLabel')}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('addHome.namePlaceholder')}
                disabled={isSubmitting}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t('addHome.nameHelper', { city: city.trim() || t('addHome.nameHelperFallback') })}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('addHome.addressLabel')}</label>
              <GoogleAddressAutocomplete
                value={address}
                onChange={setAddress}
                onPlaceSelected={(parsed) => {
                  if (parsed.city) setCity(parsed.city)
                  if (parsed.state) setState(parsed.state)
                  if (parsed.zipCode) setZip(parsed.zipCode)
                  if (parsed.country) setCountry(parsed.country)
                }}
                placeholder={t('addHome.addressPlaceholder')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addHome.cityLabel')}</label>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={t('addHome.cityPlaceholder')}
                  disabled={isSubmitting}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addHome.stateLabel')}</label>
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder={t('addHome.statePlaceholder')}
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addHome.zipLabel')}<span className="text-red-500 ml-0.5">*</span></label>
                <Input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder={t('addHome.zipPlaceholder')}
                  disabled={isSubmitting}
                  autoComplete="off"
                  maxLength={12}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('addHome.countryLabel')}</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 rounded-md border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('addHome.cancelBtn')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('addHome.addingBtn') : t('addHome.addBtn')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
